/**
 * Finance › Payouts (Repasses) under four eyes (W5-FINANCE-FOUR-EYES, D385, ADR-0055) — framework-neutral view-models
 * for the screen: which markets the queue can be read in, who issued and who decided a payout, why Approve is not
 * offered to this operator, and what each refusal code means.
 *
 * The console only FILTERS: it hides Approve from the operator who issued the payout (with the reason shown), but the
 * authority is the server, which compares the SESSION identities and answers `403 SELF_APPROVAL` whatever the console
 * shows. Money stays in integer minor units end to end; the currency of a new payout comes from the driver's market
 * (`GET /v1/payouts/context`), never typed.
 */
import { type ApiError, type FinanceApi, type LedgerOwed, type OperatingMarketView, type PayoutContext, type PayoutInput, type PayoutIssued, type PayoutRow } from '@vello/api-client';
import { type Result } from '@vello/core';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { sameRegion } from './scope.js';

/** The queue's statuses, in lifecycle order (the server's `PAYOUT_STATUSES`). */
export const PAYOUT_QUEUE_STATUSES = ['REQUESTED', 'PROCESSING', 'PAID', 'FAILED', 'REVERSED'] as const;

export type PayoutTone = 'neutral' | 'success' | 'warning' | 'danger';

export function payoutStatusTone(status: string): PayoutTone {
  return status === 'PAID' ? 'success' : status === 'REQUESTED' ? 'warning' : status === 'FAILED' || status === 'REVERSED' ? 'danger' : 'neutral';
}

/**
 * The markets whose payout queue this operator may read: a GLOBAL scope reaches every registered market, a country
 * scope its markets, a city scope that city. Sorted by display name.
 */
export function payoutMarketChoices(allowed: readonly { readonly country?: string; readonly region?: string; readonly city?: string }[], markets: readonly OperatingMarketView[]): readonly OperatingMarketView[] {
  const global = allowed.some((s) => s.country === undefined && s.city === undefined);
  // A state entry reaches only the markets registered in that state - never its whole country.
  const reach = (m: OperatingMarketView): boolean =>
    global ||
    allowed.some((s) => {
      if (s.city !== undefined) return s.city === m.id;
      if (s.region !== undefined) return sameRegion(s.region, m.regionCode);
      return s.country !== undefined && s.country.toUpperCase() === m.countryCode.toUpperCase();
    });
  return markets.filter(reach).slice().sort((a, b) => a.name.localeCompare(b.name));
}

/** Why Approve is NOT offered to this operator on this payout, or undefined when it is. Display only. */
export type PayoutApprovalBlock = 'NOT_PENDING' | 'UNATTRIBUTED' | 'OWN_PAYOUT' | 'OWN_BENEFIT' | 'NO_PERMISSION';

/** Ids are uuids the server compares case-insensitively. */
const sameId = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/** D868: `selfApproval` lifts OWN_PAYOUT (the maker rule) for a super administrator; OWN_BENEFIT (a payout to oneself) stays. */
export function payoutApprovalBlock(row: PayoutRow, operatorId: string | undefined, mayDecide: boolean, selfApproval = false): PayoutApprovalBlock | undefined {
  if (row.status !== 'REQUESTED') return 'NOT_PENDING';
  const maker = row.requestedBy ?? '';
  if (maker === '') return 'UNATTRIBUTED';
  if (!selfApproval && operatorId !== undefined && sameId(maker, operatorId)) return 'OWN_PAYOUT';
  // D385 review W5F-01: an operator who also drives never approves a payout to himself (`SELF_PAYOUT`).
  if (operatorId !== undefined && sameId(row.driverId, operatorId)) return 'OWN_BENEFIT';
  if (!mayDecide) return 'NO_PERMISSION';
  return undefined;
}

/**
 * Whether this operator may reject a payout: any REQUESTED one — by a checker, or by its own maker (a withdrawal: it
 * moves no money).
 *
 * W6-EARNINGS-LEDGER A2 (D392) closes W5F-06: an unattributed legacy payout can never be APPROVED, but it CAN be
 * rejected. Before this the console offered nothing on such a row and the server refused a rejection too, so a
 * pre-D385 payout sat in the queue for ever with no way out.
 */
export function payoutRejectable(row: PayoutRow, mayDecide: boolean): boolean {
  return mayDecide && row.status === 'REQUESTED';
}

/** Who acted, as the screen names them: the operator itself, a known sign-in, a short id, or nobody yet. */
export type PayoutActor = { readonly kind: 'you' } | { readonly kind: 'named'; readonly name: string } | { readonly kind: 'none' };

export function payoutActor(id: string | null | undefined, label: string | null | undefined, operatorId: string | undefined): PayoutActor {
  if (id === null || id === undefined || id === '') return { kind: 'none' };
  if (operatorId !== undefined && id === operatorId) return { kind: 'you' };
  if (label !== null && label !== undefined && label !== '') return { kind: 'named', name: label };
  return { kind: 'named', name: id.length > 8 ? `${id.slice(0, 8)}…` : id };
}

/** The operator's authority on the payout surface, from its grants (unknown grants → the server decides). */
export function payoutAuthority(grants: KnownGrants): { readonly mayIssue: boolean; readonly mayDecide: boolean } {
  if (grants === undefined) return { mayIssue: true, mayDecide: true };
  return {
    mayIssue: grantsAllow(grants, { permission: 'payout:create', target: 'PLATFORM' }),
    mayDecide: grantsAllow(grants, { permission: 'payout:approve', target: 'PLATFORM' }),
  };
}

/** The issue context: the driver's market and the currency it uses (`payout:create`). */
export function loadPayoutContext(runtime: { readonly finance: FinanceApi }, driverId: string): Promise<Result<PayoutContext, ApiError>> {
  return runtime.finance.payoutContext(driverId.trim());
}

/**
 * Issue a driver payout — the maker is the session; the amount is integer minor units of the context currency. D412: the
 * idempotency key names this issue ATTEMPT (see {@link payoutIssueAttempt}), so a retry after a lost answer replays the
 * payout the server already wrote instead of issuing a second one.
 */
export function issueDriverPayout(runtime: { readonly finance: FinanceApi }, input: { readonly driverId: string; readonly amountMinor: bigint; readonly currency: string; readonly idempotencyKey: string }): Promise<Result<PayoutIssued, ApiError>> {
  const body: PayoutInput = { driverId: input.driverId.trim(), amountMinor: input.amountMinor.toString(), currency: input.currency, idempotencyKey: input.idempotencyKey };
  return runtime.finance.createPayout(body);
}

/** D412: one issue attempt — the key and the request it was minted for. */
export interface PayoutIssueAttempt {
  readonly key: string;
  readonly fingerprint: string;
}

/**
 * D412: the idempotency key for an issue. The SAME request (driver, amount, currency) keeps the key of the attempt in
 * flight — pressing "issue" again after a timeout or a network error replays it, never a second payout; any change to the
 * request mints a new key (a new payout). The screen drops the attempt once the server answered it (201/200).
 */
export function payoutIssueAttempt(current: PayoutIssueAttempt | undefined, input: { readonly driverId: string; readonly amountMinor: bigint; readonly currency: string }, mint: () => string): PayoutIssueAttempt {
  const fingerprint = `${input.driverId.trim().toLowerCase()}|${input.amountMinor.toString()}|${input.currency}`;
  return current !== undefined && current.fingerprint === fingerprint ? current : { key: mint(), fingerprint };
}

// ---- W6-EARNINGS-LEDGER A2 (D392): what the ledger owes, and the settlement rail ---------------------------------

/**
 * The cap a new payout may draw, as a `bigint`, or undefined when the server reports no ledger (a pre-A2 server, or
 * one with no governed currency). Undefined means "the desk does not know" — never "unlimited": the screen then asks
 * for an amount as before and the SERVER refuses anything above the balance.
 */
export function payoutCapMinor(owed: LedgerOwed | null | undefined): bigint | undefined {
  if (owed === null || owed === undefined) return undefined;
  return /^-?\d+$/.test(owed.availableMinor) ? BigInt(owed.availableMinor) : undefined;
}

/** Whether an amount a person typed is within what the ledger owes (display only — the server is the authority). */
export function payoutWithinCap(amountMinor: bigint | undefined, cap: bigint | undefined): boolean {
  return amountMinor === undefined || cap === undefined || amountMinor <= cap;
}

/** Whether the desk may offer the settlement actions on this row: an APPROVED payout awaiting its transfer. */
export function payoutSettleable(row: PayoutRow, maySettle: boolean): boolean {
  return maySettle && row.status === 'PROCESSING';
}

/** Whether this operator holds the settlement word (`payout:settle`); unknown grants mean the server decides. */
export function payoutSettleAuthority(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'payout:settle', target: 'PLATFORM' });
}

/** A2: the transfer happened — record the rail own reference (the server posts the settlement). */
export function markPayoutPaid(
  runtime: { readonly finance: FinanceApi },
  payoutId: string,
  input: { readonly externalRef: string; readonly market?: string },
): Promise<Result<PayoutRow, ApiError>> {
  return runtime.finance.markPayoutPaid(payoutId, { externalRef: input.externalRef.trim(), ...(input.market !== undefined && input.market !== '' ? { market: input.market } : {}) });
}

/** A2: the transfer failed — the reservation returns to the payee, who can be paid again. */
export function markPayoutFailed(
  runtime: { readonly finance: FinanceApi },
  payoutId: string,
  input: { readonly reason: string; readonly market?: string },
): Promise<Result<PayoutRow, ApiError>> {
  return runtime.finance.markPayoutFailed(payoutId, { reason: input.reason.trim(), ...(input.market !== undefined && input.market !== '' ? { market: input.market } : {}) });
}
